import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';

function StaffSkillSubmission({ playerId }) {
  console.log('StaffSkillSubmission playerId:', playerId);
  const [staffList, setStaffList] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [skillGuides, setSkillGuides] = useState([]);
  const [form, setForm] = useState({
    staffId: '',
    skillName: '',
    level: '1',
    itemName: '',
    evidence: ''
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [staffRes, invRes, subRes, guideRes] = await Promise.all([
          fetch(`${API_BASE_URL}/staff`, { headers: { 'player-id': playerId } })
            .then(res => res.ok ? res : Promise.reject(`Staff fetch failed: ${res.status} ${res.statusText}`)),
          fetch(`${API_BASE_URL}/my-inventory/${playerId}`, { headers: { 'player-id': playerId } })
            .then(res => res.ok ? res : Promise.reject(`Inventory fetch failed: ${res.status} ${res.statusText}`)),
          fetch(`${API_BASE_URL}/skill-submissions`, { headers: { 'player-id': playerId } })
            .then(res => res.ok ? res : Promise.reject(`Submissions fetch failed: ${res.status} ${res.statusText}`)),
          fetch(`${API_BASE_URL}/skill-guides`, { headers: { 'player-id': playerId } })
            .then(res => res.ok ? res : Promise.reject(`Skill guides fetch failed: ${res.status} ${res.statusText}`))
        ]);
        const [staffData, invData, subData, guideData] = await Promise.all([
          staffRes.json(),
          invRes.json(),
          subRes.json().catch(() => []),
          guideRes.json()
        ]);
        console.log('Staff data:', staffData);
        console.log('Inventory data:', invData);
        console.log('Submissions data:', subData);
        console.log('Skill guides data:', guideData);
        setStaffList(staffData);
        setInventory(invData.items || []);
        // Normalize dismissed to boolean and filter
        const normalizedSubmissions = subData.map(s => ({
          ...s,
          dismissed: s.dismissed === true // Ensure boolean
        }));
        console.log('Normalized submissions:', normalizedSubmissions);
        setSubmissions(
          normalizedSubmissions.filter(
            s => s.status === 'Pending' || (s.status === 'Denied' && !s.dismissed)
          )
        );
        setSkillGuides(guideData);
      } catch (error) {
        console.error('Fetch error:', error);
        setMessage(`Error: ${error.message || 'Failed to load data'}`);
      } finally {
        setLoading(false);
      }
    };
    if (playerId) fetchData();
  }, [playerId]);

  const getLevelTitle = (skillName, level) => {
    const guide = skillGuides.find(g => g.name === skillName);
    const levelData = guide?.levels.find(l => l.level === parseInt(level));
    return levelData?.title || `Level ${level}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const submitSkill = async () => {
    try {
      if (!form.staffId || !form.skillName || !form.level || !form.itemName || !form.evidence) {
        throw new Error('All fields are required');
      }
      const res = await fetch(`${API_BASE_URL}/skill-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify({ ...form, level: parseInt(form.level) })
      });
      console.log('Submit skill response status:', res.status);
      const data = await res.json();
      console.log('Submit skill response data:', data);
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setSubmissions(prev => [...prev, { ...data, dismissed: false }].filter(
        s => s.status === 'Pending' || (s.status === 'Denied' && !s.dismissed)
      ));
      const invRes = await fetch(`${API_BASE_URL}/my-inventory/${playerId}`, {
        headers: { 'player-id': playerId }
      });
      if (!invRes.ok) throw new Error('Failed to refresh inventory');
      const invData = await invRes.json();
      setInventory(invData.items || []);
      setMessage('Skill submitted successfully!');
      setForm({
        staffId: '',
        skillName: '',
        level: '1',
        itemName: '',
        evidence: ''
      });
    } catch (error) {
      console.error('Submit skill error:', error);
      setMessage('Error: ' + (error.message || 'Submission failed'));
    }
  };

  const dismissSubmission = async (submissionId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/skill-submissions/${submissionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify({ dismissed: true })
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Dismiss failed');
      setSubmissions(prev => prev.filter(s => s._id !== submissionId));
      const invRes = await fetch(`${API_BASE_URL}/my-inventory/${playerId}`, {
        headers: { 'player-id': playerId }
      });
      if (!invRes.ok) throw new Error('Failed to refresh inventory');
      const invData = await invRes.json();
      setInventory(invData.items || []);
      setMessage('Submission dismissed');
    } catch (error) {
      console.error('Dismiss error:', error);
      setMessage('Error: ' + (error.message || 'Dismiss failed'));
    }
  };

  if (!playerId) return <p>Please log in to submit skills.</p>;
  if (loading) return <p>Loading...</p>;

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1 style={{ textAlign: 'center', color: '#4CAF50' }}>Submit Staff Skill</h1>
      {message && (
        <p style={{ color: message.includes('Error') ? 'red' : '#4CAF50', textAlign: 'center' }}>{message}</p>
      )}
      <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ color: '#4CAF50' }}>New Submission</h2>
        <div style={{ display: 'grid', gap: '10px' }}>
          <select
            name="staffId"
            value={form.staffId}
            onChange={handleInputChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
          >
            <option value="">Select Staff</option>
            {staffList.map(staff => (
              <option key={staff._id} value={staff._id}>{staff.staffName}</option>
            ))}
          </select>
          <select
            name="skillName"
            value={form.skillName}
            onChange={handleInputChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
          >
            <option value="">Select Skill</option>
            {['Animal Husbandry', 'Dog Grooming', 'Animal Medicine', 'Canine Evaluation'].map(skill => (
              <option key={skill} value={skill}>{skill}</option>
            ))}
          </select>
          <select
            name="level"
            value={form.level}
            onChange={handleInputChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
            disabled={!form.skillName}
          >
            <option value="">Select Level</option>
            {form.skillName && skillGuides.find(g => g.name === form.skillName)?.levels.map(l => (
              <option key={l.level} value={l.level}>{`Level ${l.level} (${l.title})`}</option>
            ))}
          </select>
          <select
            name="itemName"
            value={form.itemName}
            onChange={handleInputChange}
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc' }}
          >
            <option value="">Select Item</option>
            {inventory.map(item => (
              <option key={item.itemId} value={item.name}>{item.name}</option>
            ))}
          </select>
          <textarea
            name="evidence"
            value={form.evidence}
            onChange={handleInputChange}
            placeholder="Evidence (e.g., description or link)"
            style={{ padding: '8px', borderRadius: '5px', border: '1px solid #ccc', height: '100px' }}
          />
          <button
            onClick={submitSkill}
            style={{
              backgroundColor: '#4CAF50',
              color: 'white',
              padding: '10px',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Submit Skill
          </button>
        </div>
      </div>
      <div style={{ background: '#f9f9f9', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h2 style={{ color: '#4CAF50' }}>Pending and Denied Skills</h2>
        {submissions.length > 0 ? (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {submissions.map(sub => (
              <li key={sub._id} style={{ marginBottom: '10px', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }}>
                <p><strong>Staff:</strong> {sub.staffName || sub.staffId?.staffName || 'Unknown'}</p>
                <p><strong>Skill:</strong> {sub.skillName} ({getLevelTitle(sub.skillName, sub.level)})</p>
                <p><strong>Item:</strong> {sub.outfitItem || 'None'}</p>
                <p><strong>Evidence:</strong> {sub.evidence || 'None'}</p>
                <p><strong>Status:</strong> {sub.status}</p>
                {sub.status === 'Denied' && (
                  <>
                    <p><strong>Deny Reason:</strong> {sub.denyReason || 'None'}</p>
                    <button
                      onClick={() => dismissSubmission(sub._id)}
                      style={{
                        backgroundColor: '#EF5350',
                        color: 'white',
                        padding: '5px 10px',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      Dismiss
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No pending or denied skills.</p>
        )}
      </div>
    </div>
  );
}

export default StaffSkillSubmission;